import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import SharedCalendar from '../../shared/calendar/shared-calendar.component';
import { CalendarDay, CalendarLegend, CalendarMonth } from '../../../models/organization';
import { formatMonthKey, formatNumber } from 'utils/format';

// The holiday colour is the one the lab details dialog already uses
// (components/connect-to-lab/lab-details-dialog/lab-holidays.component.tsx), so the two
// views of the same facts agree.
const AVAILABLE_COLOR = '#7dc87fff';
const UNAVAILABLE_COLOR = '#f07a71ff';
const HOLIDAY_COLOR = '#FF9800';
const REMAINING_COLOR = '#e0e0e0';

type LabAvailabilityCalendarProps = {
    monthlyAvailability: monthlyAvailability[] | [];
};

type monthlyAvailability = {
    availableDays: number;
    availablityPercent: number;
    month: string;
    monthlyDays: number;
    unavailableDates: string[];
    holidayDates: string[];
    availableDates: string[];
    startDate: string;
};

type DayStyle = Pick<CalendarDay, 'color' | 'background' | 'textColor'>;

// A day is painted from the explicit sets the backend returns. Anything it does not name
// stays "remaining": a weekday the lab is closed, a day before the clinic connected, or a
// day still to come this month.
const getDayStyle = (
    dayNumber: number,
    availableDays: Set<string>,
    unavailableDays: Set<string>,
    holidayDays: Set<string>,
): DayStyle => {
    const dayKey = dayNumber.toString();
    const isHoliday = holidayDays.has(dayKey);
    const isUnavailable = unavailableDays.has(dayKey);

    if (isHoliday && isUnavailable) {
        return {
            background: `linear-gradient(90deg, ${HOLIDAY_COLOR} 50%, ${UNAVAILABLE_COLOR} 50%)`,
            textColor: 'white',
        };
    }
    if (isHoliday) return { color: HOLIDAY_COLOR, textColor: 'white' };
    if (isUnavailable) return { color: UNAVAILABLE_COLOR, textColor: 'white' };
    if (availableDays.has(dayKey)) return { color: AVAILABLE_COLOR, textColor: 'white' };

    return { color: REMAINING_COLOR, textColor: 'black' };
};

const LabAvailabilityCalendar: FC<LabAvailabilityCalendarProps> = ({ monthlyAvailability }) => {
    const { t: translate } = useTranslation(['common', 'organization', 'users']);

    const transformToCalendarData = (): CalendarMonth[] => {
        return monthlyAvailability.map((monthData) => {
            const { month, monthlyDays, unavailableDates, holidayDates, availableDates } = monthData;

            const availableDays = new Set(availableDates ?? []);
            const unavailableDays = new Set(unavailableDates ?? []);
            const holidayDays = new Set(holidayDates ?? []);

            const days: CalendarDay[] = Array.from({ length: monthlyDays }, (_, dayIndex) => {
                const dayNumber = dayIndex + 1;
                return {
                    day: dayNumber,
                    ...getDayStyle(dayNumber, availableDays, unavailableDays, holidayDays),
                };
            });

            // formatNumber keeps the old rounding (whole numbers stay whole, otherwise one
            // decimal) while using the user's decimal separator, so Swedish reads 96,7 not 96.7.
            const subtitle = `${formatNumber(monthData.availablityPercent, {
                maximumFractionDigits: 1,
            })}% ${translate('organization:AVAILABLE')}`;

            return {
                // The month arrives as the API's English key. Localize it for display, and pass
                // the day count along with it: SharedCalendar otherwise re-derives that count by
                // parsing the month name as English, which a localized name breaks. DD-3807.
                month: formatMonthKey(month),
                days,
                subtitle,
                totalDays: monthlyDays,
            };
        });
    };

    const legend: CalendarLegend[] = [
        { color: AVAILABLE_COLOR, label: translate('organization:AVAILABLE') },
        { color: UNAVAILABLE_COLOR, label: translate('organization:UNAVAILABLE') },
        { color: HOLIDAY_COLOR, label: translate('organization:HOLIDAY') },
        { color: REMAINING_COLOR, label: translate('organization:REMAINING') },
    ];

    return <SharedCalendar months={transformToCalendarData()} legend={legend} />;
};

export default LabAvailabilityCalendar;
